import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MeterService } from '../../services/meter.service';
import { ValidationService } from '../../services/validation.service';
import { HttpParams } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { keycloakInstance } from '../../keycloak.config';

interface MeterDetails {
  id: string;
  value: string;
  eligible: boolean;
  explanation: string;
  subsidyAmount: string;
  status: string; // Can be 'success', 'rejected', or 'pending'
  // New fields
  kad: string;
  kadDescription: string;
  consumption: number;
  subsidyPerKwh: number;
  applicable_ratio?: number; // Added this property
}

interface MeterInput {
  id: number;
  value: string;
  selected: boolean;
   xrhsh?: string;  // Usage type (e.g., "Οικιακή", "Εμπορική")
  type?: string;   // Meter type
}

interface ValidationResponse {
  is_eligible: boolean;
  supply_list: {
    supplyNumber: string;
  }[];
}

interface RelatedCompany {
  vat: string;
  name: string;
}

interface DeclarationRequest {
  subsidy: boolean;
  sector: boolean;
  energy: boolean;
  company: boolean;
  confirm: boolean;
  pair?: {
    vat_number: string;
    company: string;
  }[];
}

interface PreviousApplication {
  _id: string;
  aa_number: string;
  datetime: string;
  submit: string;
  submit_number: string;
  subsidy: Array<{
    annual_turnover: number;
    business_activity_code: string;
    consumption_kwh: number;
    deminimis_limit: boolean;
    description: string;
    meter_id: string;
    status: string; // Can be 'success', 'rejected', or 'pending'
    supply_type: string;
    total_subsidy: number;
  }>;
  uuid: string;
  vat_number: string;
}


interface PersonalInfoRequest {
  first_name: string;
  last_name: string;
  mobile_number: string;
  email: string;
}

@Component({
  selector: 'app-input-meters',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule,MatIconModule],
  templateUrl: './input-meters.component.html',
  styleUrls: ['./input-meters.component.scss']
})
export class InputMetersComponent implements OnInit {
  title = 'ΟΙ ΠΑΡΟΧΕΣ ΣΑΣ';
  currentStep = 1;
  showTerms = true;
  loadingProgress = false;
  isValidAFM = true;

  firstTermsText = '';
  secondTermsText = '';
  firstTermsAccepted = false;
  secondTermsAccepted = false;
  firstTermsScrolled = false;
  secondTermsScrolled = false;
  
  @ViewChild('firstTermsScroll') firstTermsScrollElement!: ElementRef;
  @ViewChild('secondTermsScroll') secondTermsScrollElement!: ElementRef;
  
  selectAll = false;

  inputs: MeterInput[] = [];

  relatedCompanies: RelatedCompany[] = [];

  personalInfoForm!: FormGroup;
  isFormSubmitting = false;
  isFormSuccess = false;
  formErrorMessage = '';

  hasPreviousApplications = false;
previousApplications: any[] = [];
showRecallDialog = false;
showSuccessDialog = false;
successMessage = '';

  confirmationItems = [
    { text: 'Δεν αιτείται επιδότηση για παροχές, που έχουν χαρακτηριστεί αγροτικές ή είναι κοινόχρηστες, καθώς και παροχές που εξυπηρετούν δραστηριότητες με κύριο κωδικό αριθμό δραστηριότητας (ΚΑΔ) στoυς τομείς της πρωτογενούς παραγωγής γεωργικών προϊόντων ή/και προϊόντων αλιείας και υδατοκαλλιέργειας', checked: false },
    { text: 'Δεν δραστηριοποιείται στους εξαιρούμενους τομείς', checked: false },
    { text: 'Για τους μήνες Δεκέμβριο του έτους 2024, Ιανουάριο και Φεβρουάριο του έτους 2025 ήταν συμβεβλημένη σε κυμαινόμενα τιμολόγια προμήθειας ηλεκτρικής ενέργειας με πράσινο ή κίτρινο χρώμα, όπως ορίζονται με την ΥΠΕΝ/ΔΗΕ /120637/2107/21.11.2023, όπως ισχύει.', checked: false },
    { text: 'Συνιστά ενιαία επιχείρηση με άλλη επιχείρηση ( ορισμός στην ΚΥΑ)', checked: false },
    { text: 'Τα στοιχεία που δήλωσα είναι ακριβή και αληθή', checked: false }
  ];
  previousRequestID: any;
  requestID: any;
  previousApplication!: PreviousApplication;
  showPopup!: boolean;
  showImmediateRecallPrompt!: boolean;
  errorMessage: any;
  isLoading!: boolean;
  hasError!: boolean;
  validationResults: any;
  meterDetails!: MeterDetails[];

  constructor(
    private validationService: ValidationService,
    private router: Router,
    private meterService: MeterService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
this.firstTermsText = `

Με ατομική μου ευθύνη και γνωρίζοντας τις κυρώσεις, που προβλέπονται από τις
διατάξεις της παρ. 6 του άρθρου 22 του Ν. 1599/1986 δηλώνω ότι συναινώ και
παρέχω την εντολή και εξουσιοδότηση να ελεγχθεί η συνδρομή των
προϋποθέσεων και κωλυμάτων της αιτούμενης επιδότησης σύμφωνα με την ΚΥΑ
ΦΕΚ Β 2212/07.05.2025 μέσω της άντλησης και διασταύρωσης των στοιχείων της
αιτούσας επιχείρησης με τα στοιχεία άλλων υπηρεσιών και ηλεκτρονικών
μητρώων των φορέων του δημοσίου τομέα, από τις ακόλουθες διαδικτυακές
πηγές και με βάση τα στοιχεία που περιλαμβάνονται σε αυτές, συγκεκριμένα:
1. Μέσω του Κέντρου Διαλειτουργικότητας (ΚΕΔ) της Γενικής Γραμματείας
Πληροφοριακών Συστημάτων και Ψηφιακής Διακυβέρνησης η Διαδικτυακή
Υπηρεσία Αυθεντικοποίησης χρηστών με τη χρήση διαπιστευτηρίων
(κωδικών) TAXISnet
2. Μέσω του ΚΕΔ της Γενικής Γραμματείας Πληροφοριακών Συστημάτων και
Ψηφιακής Διακυβέρνησης, η Διαδικτυακή Υπηρεσία της Ανεξάρτητης Αρχής
Δημοσίων Εσόδων (ΑΑΔΕ) για την επιβεβαίωση του τομέα της κύριας
επιχειρηματικής δραστηριότητας και των δηλωθέντων ακαθάριστων
εσόδων φορολογικού έτους 2023 της επιχείρησης που αιτείται να καταστεί
δικαιούχος.
3. Μέσω της Διαδικτυακής Υπηρεσίας με το Πληροφοριακό Σύστημα
Σώρευσης Κρατικών ενισχύσεων Ήσσονος Σημασίας (ΠΣΣΚΕΗΣ), στο οποίο
καταχωρίζονται τα δεδομένα που αφορούν τον έλεγχο σώρευσης για την
παρακολούθηση της τήρησης των Κανονισμών Κρατικών Ενισχύσεων
Ήσσονος Σημασίας (ΕΕ) της εκάστοτε επιχείρησης που αιτείται να καταστεί
δικαιούχος της επιδότησης, με εξαίρεση επιχειρήσεις που διαθέτουν
παροχές, που έχουν χαρακτηριστεί αγροτικές ή κοινόχρηστες, καθώς και
παροχές επιχειρήσεων με κύριο ΚΑΔ στoυς τομείς της πρωτογενούς
παραγωγής γεωργικών προϊόντων ή/και προϊόντων αλιείας και
υδατοκαλλιέργειας.
4. Μέσω Διαδικτυακής Υπηρεσίας στοιχείων παροχών ηλεκτρικού ρεύματος
του ΔΕΔΔΗΕ από την οποία αντλούνται και διασταυρώνονται οι ακόλουθες
πληροφορίες: i. ο αριθμός/οι αριθμοί παροχής ηλεκτρικού ρεύματος που
συνδέεται με το ΑΦΜ της επιχείρησης ii. χαρακτηριστικά της παροχής.
Ενόψει των ανωτέρω έχω ενημερωθεί και συναινώ στα παρακάτω:
Α. Οι ως άνω επεξεργασίες των υπό 1 έως 4 δεδομένων διενεργούνται από το
ΔΕΔΔΗΕ, το ΚΕΔ και το Πληροφοριακό Σύστημα Σώρευσης Κρατικών Ενισχύσεων
Ήσσονος Σημασίας (ΠΣΣΚΕΗΣ) ως απαραίτητες, για να ληφθούν μέτρα κατ’ αίτηση
του υποκείμενου των δεδομένων.
Β. Σκοπός της επεξεργασίας είναι η άντληση και διασταύρωση των αναφερόμενων
υπό 1 έως 4 ανωτέρω στοιχείων μέσω των ανωτέρω διαδικτυακών υπηρεσιών
για την αξιολόγηση της αίτησής μου.
Γ. Συναινώ στην επεξεργασία των δεδομένων προσωπικού χαρακτήρα μου για τον
ανωτέρω σκοπό (άρθρο 6, παρ. 1α ΓΚΠΔ).
Δ. Αναγνωρίζω ότι κατά την υποβολή της αίτησης γίνεται άντληση και
διασταύρωση των αναφερόμενων υπό 1 έως 4 ανωτέρω στοιχείων μέσω των
ανωτέρω διαδικτυακών υπηρεσιών και εφόσον: α) είτε δεν είναι δυνατή η
διασταύρωση - επιβεβαίωση των απαιτούμενων στοιχείων για την αξιολόγηση
μίας αίτησης, β) είτε από τα στοιχεία που αντλούνται επιβεβαιώνεται η μη
επιλεξιμότητα της αίτησης, η αίτηση δε δύναται να προωθηθεί προς αξιολόγηση.
Ε. Η επιχείρηση βάσει της (υπ΄ αριθμ. ΥΠΕΝ/ΓΔΕ/49043/1893 Κοινής Υπουργικής
Απόφασης ΦΕΚ Β’ 2212/07.05.2025), αφορά δραστηριότητες της επιχείρησης που
δεν εμπίπτουν:
i) στην πρωτογενή παραγωγή προϊόντων αλιείας και της υδατοκαλλιέργειας,
ii) στη μεταποίηση και εμπορία προϊόντων αλιείας και υδατοκαλλιέργειας,
εφόσον το ποσό της ενίσχυσης καθορίζεται με βάση την τιμή ή την ποσότητα
των προϊόντων που αγοράζονται ή διατίθενται στην αγορά,
iii) στην πρωτογενή παραγωγή γεωργικών προϊόντων,
iv) στον τομέα της μεταποίησης και της εμπορίας γεωργικών προϊόντων, όταν το
ποσό της ενίσχυσης καθορίζεται με βάση την τιμή ή την ποσότητα τέτοιων
προϊόντων που πωλούνται από πρωτογενείς παραγωγούς ή διατίθενται στην
αγορά από τις οικείες επιχειρήσεις ή όταν η ενίσχυση συνοδεύεται από την
υποχρέωση απόδοσής της εν μέρει ή εξ ολοκλήρου σε πρωτογενείς
παραγωγούς,
v) εξαγωγές προς τρίτες χώρες ή προς κράτη μέλη, ιδίως δε ενισχύσεις που
συνδέονται άμεσα με τις εξαγόμενες ποσότητες, με τη δημιουργία και
λειτουργία δικτύου διανομής ή με άλλες τρέχουσες δαπάνες που σχετίζονται με
την εξαγωγική δραστηριότητα,
vi) ενισχύσεις για τις οποίες τίθεται ως όρος η χρήση εγχώριων αγαθών και
υπηρεσιών αντί των εισαγόμενων.
Ζ. (Σε περίπτωση που η επιχείρηση δραστηριοποιείται σε κάποιον από τους μη
επιλέξιμους για ενίσχυση τομείς και επίσης σε τομέα επιλέξιμο για ενίσχυση
βάσει του Κανονισμού (ΕΕ) 2023/2831)
Η επιχείρηση, καθώς δραστηριοποιείται στον τομέα (στους τομείς) ο
οποίος (οι οποίοι) είναι μη επιλέξιμοι για ενίσχυση, διασφαλίζει με
κατάλληλα μέσα, όπως ο λογιστικός διαχωρισμός, ότι δεν ενισχύεται η μη
επιλέξιμη δραστηριότητα.
Η. Η επιχείρηση δεν έχει λάβει άλλη κρατική ενίσχυση για τις ίδιες επιλέξιμες
δαπάνες ή για το ίδιο μέτρο χρηματοδότησης επιχειρηματικού κινδύνου, η
σώρευση των οποίων οδηγεί σε υπέρβαση της υψηλότερης σχετικής έντασης
ενίσχυσης ή του ποσού ενίσχυσης που έχει καθοριστεί με βάση τα συγκεκριμένα
δεδομένα κάθε περίπτωσης σε κανονισμό απαλλαγής κατά κατηγορία ή απόφαση
που έχει εκδώσει η Επιτροπή.
Θ. Αποδέχομαι οποιονδήποτε σχετικό έλεγχο για την εξακρίβωση των
δηλωθέντων στοιχείων από τις αρμόδιες εθνικές ή ενωσιακές αρχές, καθώς και
τη διασταύρωση αυτών με τα στοιχεία που παρέχονται από τα πληροφοριακά
συστήματα δημοσίων υπηρεσιών και ασφαλιστικών οργανισμών.
Για τα ανωτέρω σημειώνονται τα εξής:
Η ενίσχυση ήσσονος σημασίας θεωρείται ότι χορηγείται κατά τον χρόνο
παραχώρησης στην οικεία επιχείρηση του έννομου δικαιώματος λήψης της
ενίσχυσης σύμφωνα με το εφαρμοστέο εθνικό νομικό καθεστώς (πχ.
ημερομηνία έκδοσης εγκριτικής απόφασης/ΚΥΑ/σύμβασης), ανεξάρτητα από
την ημερομηνία καταβολής της ενίσχυσης ήσσονος σημασίας στην
επιχείρηση.
Σε περίπτωση συγχωνεύσεων ή εξαγορών, όλες οι προηγούμενες ενισχύσεις
ήσσονος σημασίας που έχουν ήδη χορηγηθεί σε οποιαδήποτε από τις
συγχωνευόμενες επιχειρήσεις λαμβάνονται υπόψη για να προσδιοριστεί κατά
πόσο η νέα ενίσχυση ήσσονος σημασίας στη νέα ή στην εξαγοράζουσα
επιχείρηση υπερβαίνει το σχετικό ανώτατο όριο.
Αν μια επιχείρηση διασπαστεί σε δύο ή περισσότερες χωριστές
επιχειρήσεις, η ενίσχυση ήσσονος σημασίας που χορηγήθηκε πριν από τη
διάσπαση καταλογίζεται στην επιχείρηση που έλαβε αυτή την ενίσχυση, η
οποία είναι κατά κανόνα η επιχείρηση που ανέλαβε τις δραστηριότητες για τις
οποίες χρησιμοποιήθηκε η ενίσχυση ήσσονος σημασίας. Εάν ο εν λόγω
καταλογισμός δεν είναι δυνατός, οι ενισχύσεις ήσσονος σημασίας πρέπει να
κατανέμονται αναλογικά με βάση τη λογιστική αξία των ιδίων κεφαλαίων των
νέων επιχειρήσεων κατά την πραγματική ημερομηνία της διάσπασης. viii Ως
προϊόντα αλιείας και υδατοκαλλιέργειας νοούνται τα προϊόντα που ορίζονται
στο άρθρο 5 στοιχεία α) και β) του κανονισμού (ΕΕ) 1379/2013.
Ως πρωτογενής παραγωγή προϊόντων αλιείας και υδατοκαλλιέργειας
νοούνται όλες οι εργασίες που σχετίζονται με την αλιεία, την εκτροφή ή την
καλλιέργεια υδρόβιων οργανισμών, καθώς και οι δραστηριότητες εντός της
εκμετάλλευσης ή επί του σκάφους που είναι αναγκαίες για την προετοιμασία
προϊόντος ζωικής ή φυτικής προέλευσης για την πρώτη του πώληση,
συμπεριλαμβανομένων της κοπής, του τεμαχισμού σε φιλέτα ή της
κατάψυξης, καθώς και η πρώτη πώληση σε μεταπωλητές ή μεταποιητές.
Ως μεταποίηση και εμπορία προϊόντων αλιείας και υδατοκαλλιέργειας
νοούνται όλες οι δραστηριότητες, συμπεριλαμβανομένου του χειρισμού, της
κατεργασίας και του μετασχηματισμού, που πραγματοποιούνται μετά την
εκφόρτωση —ή τη συγκομιδή, στην περίπτωση της υδατοκαλλιέργειας— από
τις οποίες προκύπτει ένα μεταποιημένο προϊόν, καθώς και η διανομή του.
Ως πρωτογενής γεωργική παραγωγή νοείται η παραγωγή προϊόντων του
εδάφους και της κτηνοτροφίας που απαριθμούνται στο παράρτημα I της
Συνθήκης, χωρίς να εκτελούνται περαιτέρω εργασίες που μεταβάλλουν τη
φύση αυτών των προϊόντων.
Γεωργικά προϊόντα είναι τα προϊόντα που απαριθμούνται στο παράρτημα I
της Συνθήκης, με εξαίρεση τα προϊόντα αλιείας και υδατοκαλλιέργειας που
εμπίπτουν στο πεδίο εφαρμογής του κανονισμού (ΕΕ) 1379/2013.
Ως μεταποίηση γεωργικών προϊόντων νοείται κάθε επέμβαση επί γεωργικού
προϊόντος από την οποία προκύπτει επίσης γεωργικό προϊόν, με εξαίρεση τις
εργασίες εντός της γεωργικής εκμετάλλευσης που είναι απαραίτητες για την
προετοιμασία προϊόντος ζωικής ή φυτικής προέλευσης για την πρώτη του
πώληση.
Ως εμπορία γεωργικών προϊόντων νοείται η κατοχή ή η έκθεση με σκοπό την
πώληση, την προσφορά προς πώληση, την παράδοση ή οποιονδήποτε άλλον
τρόπο διάθεσης στην αγορά, με εξαίρεση την πρώτη πώληση από μέρους
πρωτογενούς παραγωγού σε μεταπωλητές ή μεταποιητικές επιχειρήσεις και κάθε
δραστηριότητα η οποία προετοιμάζει το προϊόν για μια τέτοια πρώτη πώληση· η
πώληση από μέρους πρωτογενούς παραγωγού προς τελικούς καταναλωτές
λογίζεται ως εμπορία αν πραγματοποιείται σε χωριστό και ειδικό για τον σκοπό
αυτό χώρο.
Οι ενισχύσεις εκφράζονται ως επιχορήγηση σε μετρητά. Σε όλες τις περιπτώσεις
χρησιμοποιούνται ακαθάριστα ποσά, δηλαδή πριν αφαιρεθεί ο οποιοσδήποτε
φόρος ή άλλη επιβάρυνση. Εάν η ενίσχυση χορηγείται με μορφή άλλη από την
επιχορήγηση, ως ποσό της ενίσχυσης λογίζεται το ακαθάριστο ισοδύναμο
επιχορήγησής της. Οι ενισχύσεις που καταβάλλονται σε δόσεις ανάγονται στην αξία
τους κατά το χρόνο της χορήγησής τους. Το επιτόκιο που χρησιμοποιείται για την
αναγωγή είναι το προεξοφλητικό επιτόκιο που ισχύει κατά τον χρόνο χορήγησης
της ενίσχυσης
Ι. Δηλώνω ότι έλαβα γνώση των όρων και προϋποθέσεων της ΚΥΑ ΦΕΚ Β
2212/07.05.2025 και των Όρων Χρήσης Ασφάλειας & την Πολιτική Προστασίας
Προσωπικών Δεδομένων ( <a href="https://deddie.gr/el/oroi-xrisis-asfaleia/politiki-prostasia-prosopikon-dedomenon-2023/" target="_blank">https://deddie.gr/el/oroi-xrisis-asfaleia/politiki-prostasia-prosopikon-dedomenon-2023/</a> ) του ΔΕΔΔΗΕ και ότι όλα τα στοιχεία της
αίτησής μου είναι αληθή και ακριβή.
Για οποιαδήποτε διευκρίνιση σχετικά με τα ανωτέρω και για να ασκήσετε τα
δικαιώματά σας, μεταξύ των οποίων και το δικαίωμα ανάκλησης της παρούσας
συγκατάθεσης, μπορείτε να επικοινωνήσετε με τον ΔΕΔΔΗΕ στο 211 1900 500.
Σημειωτέον ότι η ανάκληση της συγκατάθεσης ισχύει για το μέλλον και δεν
επηρεάζει τη νομιμότητα της επεξεργασίας που τελέστηκε πριν από τη λήψη της.
`;

  
this.secondTermsText = `

Όροι Χρήσης & Ενημέρωση σχετικά με την Επεξεργασία των Προσωπικών
Δεδομένων κατά τη χρήση της Πλατφόρμας Επιδότησης Ηλεκτρικής Ενέργειας
Επιχειρήσεων
Όροι Χρήσης & Ενημέρωση σχετικά με την Επεξεργασία των Προσωπικών Δεδομένων κατά τη χρήση της Πλατφόρμας Επιδότησης Ηλεκτρικής Ενέργειας Επιχειρήσεων σχετικά με την Επεξεργασία των Προσωπικών Δεδομένων κατά τη
χρήση της Πλατφόρμας Επιδότησης Ηλεκτρικής Ενέργειας Επιτηδευματιών
1.1 Λειτουργικότητες πλατφόρμας
Η παρούσα πλατφόρμα, μέσω της οποίας οι πελάτες του ΔΕΔΔΗΕ (μη οικιακοί καταναλωτές ηλεκτρικής
ενέργειας), συνδεόμενοι στην ειδική διαδικτυακή πύλη https://www.power4business.deddie.gr με τη
χρήση κωδικών διαπιστευτηρίων της Γενικής Γραμματείας Πληροφοριακών Συστημάτων Δημόσιας
Διοίκησης του Υπουργείου Ψηφιακής Διακυβέρνησης (taxisnet), παρέχει τις παρακάτω δυνατότητες:
• Υποβολή αιτήματος για παροχή επιδότησης
• Εμφάνιση υπολογιζόμενης κατανάλωσης και εκτιμώμενου ποσού επιδότησης
• Εμφάνιση δηλωθέντος κύκλου εργασιών
• Εμφάνιση δεδομένων που αφορούν τον έλεγχο σώρευσης για την παρακολούθηση της
τήρησης των Κανονισμών Κρατικών Ενισχύσεων Ήσσονος Σημασίας (ΕΕ)
Η λειτουργία και η χρήση της παρούσας πλατφόρμας διέπονται από την εφαρμοστέα νομοθεσία, τους
παρόντες όρους χρήσης (εφεξής, οι «Όροι») και την Ενημέρωση για την Επεξεργασία Προσωπικών
Δεδομένων του ΔΕΔΔΗΕ, όπως αυτή εκάστοτε ισχύει ή και όποια άλλη Πολιτική ΔΕΔΔΗΕ σχετίζεται με
τους παρόντες Όρους Χρήσης και τη λειτουργία της πλατφόρμας.
Η χρήση της πλατφόρμας προϋποθέτει και συνεπάγεται την πλήρη κατανόηση και την ανεπιφύλακτη
αποδοχή των Όρων και τη συμμόρφωση των χρηστών με τη νομοθεσία, τις υποδείξεις και τις πολιτικές
του ΔΕΔΔΗΕ. Οι χρήστες, με την είσοδό τους στην πλατφόρμα ή με οποιαδήποτε χρήση αυτής,
τεκμαίρεται ότι έχουν αναγνωρίσει, κατανοήσει πλήρως και επιβεβαιώσει την αποδοχή των Όρων και
τη συμμόρφωσή τους με αυτούς και την ισχύουσα νομοθεσία. Περαιτέρω, με την είσοδο στην
πλατφόρμα ή με οποιαδήποτε χρήση αυτής, οι χρήστες δηλώνουν και εγγυώνται ότι έχουν
δικαιοπρακτική ικανότητα να αποδέχονται και να τηρούν τους Όρους.
Ο ΔΕΔΔΗΕ δεν εγγυάται την απρόσκοπτη λειτουργίατης πλατφόρμας, ούτε την ικανοποίηση όλων των
αιτημάτων των χρηστών μέσω της πλατφόρμας. Τέλος, ο ΔΕΔΔΗΕ δικαιούται να διακόψει ανά πάσα
στιγμή και αζημίως την πρόσβαση των χρηστών στην πλατφόρμα λόγω παραβίασης των Όρων ή της
νομοθεσίας ή για άλλο λόγο κατά την απόλυτη διακριτική του ευχέρεια.
1.2 Αποθήκευση στοιχείων
α. O εκάστοτε δυνητικός δικαιούχος/επιχείρηση, φυσικό ή νομικό πρόσωπο που επιθυμεί να
επιδοτηθεί στο πλαίσιο του μέτρου εισέρχεται σε ειδική διαδικτυακή πύλη
https://www.power4business.deddie.gr , αρμοδιότητας του Διαχειριστή Ελληνικού Δικτύου Διανομής
Ηλεκτρικής Ενέργειας (εφεξής ΔΕΔΔΗΕ), από την 12η Μαΐου 2025 έως και την 12η Ιουνίου 2025.
β. Για την υποβολή της αίτησης απαιτείται υποχρεωτικά η προηγούμενη αυθεντικοποίηση του
αιτούντος με τη χρήση των κωδικών - διαπιστευτηρίων της Γενικής Γραμματείας Πληροφοριακών
Συστημάτων Δημόσιας Διοίκησης του Υπουργείου Ψηφιακής Διακυβέρνησης (taxisnet), σύμφωνα με
το άρθρο 24 του ν. 4727/2020 (Α’184).
γ. Μετά από την ορθή σύνδεση και την ταυτοποίησή του με κωδικούς TaxisNet, ο δυνητικός δικαιούχος
δηλώνει, από τη λίστα παροχών που εμφανίζεται στην πλατφόρμα κάθε παροχή, γιατην οποία αιτείται
επιδότηση. Η αιτούσα επιχείρηση δηλώνει στην πλατφόρμα του ΔΕΔΔΗΕ ότι δεν αιτείται επιδότηση
για παροχές, που έχουν χαρακτηριστεί αγροτικές ή είναι κοινόχρηστες, καθώς και παροχές που
εξυπηρετούν δραστηριότητες με κωδικό αριθμό δραστηριότητας (ΚΑΔ) στους τομείς της πρωτογενούς
παραγωγής γεωργικών προϊόντων ή/και προϊόντων αλιείας και υδατοκαλλιέργειας. Οι αριθμοί
παροχής που εμφανίζει το σύστημα είναι ενεργές παροχές ή ήταν ενεργές για κάποιο διάστημα εντός
της περιόδου επιδότησης, με χρήση εμπορική/γενική ή βιομηχανική και είναι συνδεδεμένοι με τον
ΑΦΜ του αιτούντα στα συστήματα του ΔΕΔΔΗΕ. Για τον υπολογισμό της επιδότησης στην ενιαία
επιχείρηση ή/και σε επιχειρήσεις, για τις οποίες δε δύναται να εκτιμηθεί το ποσό της επιδότησης, ο
ΔΕΔΔΗΕ δύναται κατά περίπτωση να ζητά από την αιτούσα επιχείρηση να δηλώσει ή και να
προσκομίσει επιπλέον στοιχεία.
δ. Η ανωτέρω δήλωση του δικαιούχου επέχει τη θέση:
i. Υπεύθυνης δήλωσης του ν. 1599/1986 (Α’ 75) ως προς τα δηλωθέντα στοιχεία.
ii. Αίτησης και συναίνεσης για την άντληση, διαβίβαση και διασταύρωση στοιχείων του αιτούντος με
τα στοιχεία άλλων υπηρεσιών και ηλεκτρονικών μητρώων των φορέων του δημόσιου τομέα. Για τον
σκοπό αυτό, πριν την υποβολή της αίτησης επιδότησης προηγείται η γνωστοποίηση και ρητή
αποδοχή όλων των όρων και προϋποθέσεων από την πλευρά του ωφελούμενου, ενώ δεν παρέχεται
δυνατότητα διόρθωσης υποβληθεισών αιτήσεων.
ε. Οι πληροφορίες που αποθηκεύονται είναι: ονοματεπώνυμο, ονοματεπώνυμο πατρός,
ονοματεπώνυμο μητρός, ημερομηνία γέννησης, ΑΦΜ, τηλέφωνο, email, λίστα παροχών που απαιτεί
επιδότηση.
1.3 Χρήση πλατφόρμας
Η χρήση της πλατφόρμας παρέχεται δωρεάν, χωρίς να επιφέρει καμιά οικονομική επιβάρυνση στον
χρήστη.
1.4 Υποχρεώσεις χρηστών
Οι πελάτες/χρήστες υποχρεούνται να:
• Μη χρησιμοποιούν την πλατφόρμα για σκοπούς πέραν αυτών στους οποίους η αποσκοπεί η
δημιουργία της, ιδίως να μη χρησιμοποιούν την πλατφόρμα για παράνομους σκοπούς ή
δραστηριότητες (ενδεικτικά, απαγορεύεται η πλαστοπροσωπία, η τροποποίηση ή με
οποιοδήποτε τρόπο παραποίηση της πληροφορίας, η μετάδοση ή δημοσίευση ιών,
κακόβουλου λογισμικού ή άλλου κακόβουλου κώδικα στην πλατφόρμα και η παρεμπόδιση
της λειτουργίας ή η απενεργοποίησή της).
• Μην παραβιάζουν τα δικαιώματα τρίτων με την χρήση της πλατφόρμας.
• Εισάγουν στην πλατφόρμα σαφείς και ακριβείς πληροφορίες και ενημερώνουν αμελλητί τον
ΔΕΔΔΗΕ σε περίπτωση που οι πληροφορίες που εισήγαγαν στην πλατφόρμα ήταν ή
κατέστησαν ανακριβείς, ασαφείς ή εσφαλμένες, καθώς και σε περίπτωση που απωλέσουν την
πρόσβαση στην πλατφόρμα ή διαπιστώσουν οποιαδήποτε παραβίαση της πλατφόρμας.
• Χρησιμοποιούν τις κατάλληλες συσκευές και εξοπλισμό για τη χρήση της πλατφόρμας και
πραγματοποιούν τους ενδεδειγμένους χειρισμούς εντός της πλατφόρμας.
1.5 Περιορισμός ευθύνης
Η πλατφόρμα παρέχεται "ως έχει" χωρίς καμία εγγύηση, ρητή ή σιωπηρή. Η ΔΕΔΔΗΕ Α.Ε. δεν φέρει
καμία ευθύνη για οποιαδήποτε ζημία προκύψει από «κακή» χρήση της.
Στο βαθμό που επιτρέπεται από τη νομοθεσία, η ΔΕΔΔΗΕ Α.Ε.: (α) δεν φέρει οποιαδήποτε ευθύνη,
συμβατική, αδικοπρακτική (συμπεριλαμβανομένης της περίπτωσης αμελείας ή αντικειμενικής
ευθύνης) ή οποιαδήποτε άλλη ευθύνη σε σχέση με την πλατφόρμα, καθώς και τη χρήση της και (β) δεν
παρέχει οποιαδήποτε εγγύηση οποιουδήποτε είδους, ρητή, σιωπηρή, συμπεριλαμβανομένης,
ενδεικτικά, αναφορικά με την συνεχή λειτουργία της πλατφόρμας ή τη συνεχή δυνατότητα πρόσβασης
των χρηστών σε αυτή. Με την επιφύλαξη των ανωτέρω και της ισχύουσας νομοθεσίας, ο ΔΕΔΔΗΕ δε
φέρει οποιαδήποτε ευθύνη αναφορικά με οποιοδήποτε τεχνικό ή άλλο σφάλμα της πλατφόρμας, για
οποιαδήποτε ζημία προκληθεί στον χρήστη ή στον εξοπλισμό του κατά τη χρήση της. Τέλος, ο ΔΕΔΔΗΕ
δε φέρει καμία ευθύνη για οποιαδήποτε ζημία προκύψει από «κακή χρήση» της πλατφόρμας.
Ο ιστότοπος προορίζεται για χρήστες που έχουν συμπληρώσει τουλάχιστον το 18 ο έτος της ηλικίας
τους. Άτομα κάτω από το υπόψη όριο ηλικίας δεν επιτρέπεται να χρησιμοποιούν ή να εγγράφονται
στην Υπηρεσία
1.6 Τροποποίηση όρων
Η ΔΕΔΔΗΕ Α.Ε. διατηρεί το δικαίωμα να τροποποιεί τους παρόντες όρους χρήσης ανά πάσα στιγμή.
Οι αλλαγές θα τίθενται σε ισχύ από τη στιγμή της δημοσίευσής τους στην πλατφόρμα. Συνιστούμε να
ελέγχετε τακτικά τους όρους χρήσης για ενδεχόμενες αλλαγές.
1.7. Εφαρμοστέο Δίκαιο
Οι Όροι χρήσης και οποιαδήποτε μελλοντική τροποποίηση τους διέπονται αποκλειστικά από το
ελληνικό δίκαιο. Οποιαδήποτε διάταξη των Όρων χρήσης ή μελλοντικών τους τροποποιήσεων
καταστεί αντίθετη προς το Νόμο, παύει αυτοδικαίως να ισχύει, χωρίς σε καμία περίπτωση να θίγεται
η ισχύς των υπολοίπων όρων της παρούσας.
Για κάθε διαφορά που αφορά ή ανακύπτει από την ερμηνεία ή την εφαρμογή των παρόντων όρων και
την εν γένει χρήση της πλατφόρμας, εφόσον δεν επιλυθεί φιλικά, κάτι που είναι προαιρετικό και
συνιστά ευχέρεια, υπάγεται αποκλειστικά στη δικαιοδοσία των δικαστηρίων της Αθήνας.    
Ενημέρωση σχετικά με την Επεξεργασία των Προσωπικών Δεδομένων κατά τη
χρήση της Πλατφόρμας Επιδότησης Ηλεκτρικής Ενέργειας Επιτηδευματιών
Ι. Εισαγωγή
Ο ΔΕΔΔΗΕ Α.Ε. (εφεξής «ΔΕΔΔΗΕ» ή «η Εταιρεία») έχει ως βασική προτεραιότητα την προστασία των
προσωπικών δεδομένων τα οποία επεξεργάζεται υπό την ιδιότητα του υπευθύνου επεξεργασίας,
σύμφωνα με την ισχύουσα νομοθεσία για την προστασία δεδομένων προσωπικού χαρακτήρα. Η εν
λόγω επεξεργασία λαμβάνει χώρα υπό τις προϋποθέσεις, τις εγγυήσεις και εν γένει σύμφωνα με τον
Γενικό Κανονισμό (ΕΕ) 2016/679 για την προστασία των φυσικών προσώπων έναντι της επεξεργασίας
των δεδομένων προσωπικού χαρακτήρα και για την ελεύθερη κυκλοφορία των δεδομένων αυτών
(εφεξής «ΓΚΠΔ»), όπως ισχύει, καθώς και την ισχύουσα εθνική νομοθεσία [Ν. 4624/2019, Οδηγίες και
Αποφάσεις της Αρχής Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (εφεξής «ΑΠΔΠΧ»)] και
σύμφωνα με την παρούσα ενημέρωση και την αντίστοιχη πολιτική του ΔΕΔΔΗΕ.
Η παρούσα ενημέρωση έχει ως σκοπό να πληροφορήσει τους πελάτες του ΔΕΔΔΗΕ - μη οικιακοί
καταναλωτές ηλεκτρικής ενέργειας - (εφεξής “υποκείμενα των δεδομένων”), σχετικά με τα προσωπικά
δεδομένα που συλλέγει και επεξεργάζεται στο πλαίσιο της χρήσης της πλατφόρμας υποδοχής
αιτημάτων επιδότησης, από το Ταμείο Ενεργειακής Μετάβασης, της τιμολογητέας κατανάλωσης
ηλεκτρικής ενέργειας μη οικιακών καταναλωτών ηλεκτρικής ενέργειας, για τους μήνες Δεκέμβριο
του έτους 2024 και Ιανουάριο και Φεβρουάριο του έτους 2025 (εφεξής “η Πλατφόρμα”) του
ΔΙΑΧΕΙΡΙΣΤΗ ΕΛΛΗΝΙΚΟΥ ΔΙΚΤΥΟΥ ΔΙΑΝΟΜΗΣ ΗΛΕΚΤΡΙΚΗΣ ΕΝΕΡΓΕΙΑΣ Α.Ε. (εφεξής “ο ΔΕΔΔΗΕ”), μέσω
της οποίας αυτοί συνδέονται για να υποβάλλουν σχετικό αίτημα, καθώς και τον τρόπο και τους
σκοπούς συλλογής, αποθήκευσης και χρήσης των εν λόγω προσωπικών δεδομένων.
IΙ. Κατηγορίες δεδομένων που συλλέγουμε και επεξεργαζόμαστε κατά τη χρήση της πλατφόρμας
Όταν ο επισκέπτης/ χρήστης επισκέπτεται τον ανωτέρω δικτυακό τόπο του ΔΕΔΔΗΕ και εφ’ όσον
κάνει χρήση της υπηρεσίας υποβολής αιτήματος
συλλέγονται και τυγχάνουν επεξεργασίας οι απαιτούμενες εκ του νόμου πληροφορίες, όπως:
Α. Για την Ταυτοποίηση των «χρηστών» των ηλεκτρονικών υπηρεσιών
Η ταυτοποίηση των «χρηστών» της «υπηρεσίας» παρέχεται από το κέντρο διαλειτουργικότητας της
Γενικής Γραμματείας Πληροφοριακών Συστημάτων Δημόσιας Διοίκησης (ΓΠΣΔΔ) του Υπουργείου
Ψηφιακής Διακυβέρνησης, με τα διαπιστευτήρια που έχουν αποδοθεί στον «χρήστη» από το σύστημα
TaxisNet κατά τα οριζόμενα στη με αρ. 3981ΕΞ2020 Απόφαση του Υπουργού Επικρατείας «Παροχή
Υπηρεσίας Αυθεντικοποίησης Χρηστών ΟAuth2.0 σε Πληροφοριακά Συστήματα τρίτων Φορέων» (ΦΕΚ
Β’ 762/10.3.2020). Με αποκλειστικό σκοπό την ταυτοποίηση των «χρηστών» που χρησιμοποιούν την
«υπηρεσία», το Υπουργείο λαμβάνει μέσω της ως άνω ταυτοποίησης (ΟAuth 2.0), και επεξεργάζεται –
ως υπεύθυνος επεξεργασίας – τα ακόλουθα δεδομένα προσωπικού χαρακτήρα των υποκειμένων των
δεδομένων:
• Ονοματεπώνυμο
• Ονοματεπώνυμο πατρός
• Ονοματεπώνυμο μητρός
• Ημερομηνία γέννησης
• ΑΦΜ
Β. Για την Παροχή των ηλεκτρονικών υπηρεσιών
Για την χρήση της «υπηρεσίας» και την αποτελεσματική και σύννομη παροχή της υπηρεσίας αυτής ο
ΔΕΔΔΗΕ, επεξεργάζεται – ως υπεύθυνος επεξεργασίας – τα ακόλουθα δεδομένα:
• Αριθμό/οί παροχής ηλεκτρικής ενέργειας
• Κωδικός κύριας επιχειρηματικής δραστηριότητας
• Κύκλος εργασιών – Κωδ. 47 του Ε3
• Δεδομένα που αφορούν τον έλεγχο σώρευσης για την παρακολούθηση της τήρησης των
Κανονισμών Κρατικών Ενισχύσεων Ήσσονος Σημασίας (ΕΕ)
• Κάθε άλλο στοιχείο που ορίζεται στην ΚΥΑ ΥΠΕΝ/ΓΔΕ/49043/1893/07.05.2025
Γ. Ειδικές Κατηγορίες δεδομένων προσωπικού χαρακτήρα
Ο ΔΕΔΔΗΕ δε συλλέγει, ούτε επεξεργάζεται ειδικές κατηγορίες δεδομένων, όπως ορίζονται στις
διατάξεις της κείμενης νομοθεσίας (ιδίως φυλετική ή εθνική καταγωγή, θρήσκευμα, δεδομένα υγείας
κλπ.) για τις ανάγκες ταυτοποίησης και παροχής της υπηρεσίας.
ΙΙ. Σκοπός της επεξεργασίας
Ο ΔΕΔΔΗΕ συλλέγει και επεξεργάζεται τα ανωτέρω προσωπικά δεδομένα με σκοπό τον καθορισμό των
δικαιούχων μη οικιακών καταναλωτών ηλεκτρικής ενέργειας, για την παροχή επιδότησης της
τιμολογητέας κατανάλωσης ηλεκτρικής ενέργειας από τον ειδικό λογαριασμό με την ονομασία
«Ταμείο Ενεργειακής Μετάβασης» για τους μήνες Δεκέμβριο του έτους 2024 και Ιανουάριο και
Φεβρουάριο του έτους 2025.
ΙV. Νομική βάση της επεξεργασίας
Ο ΔΕΔΔΗΕ επεξεργάζεται τα ανωτέρω δεδομένα προσωπικού χαρακτήρα στο πλαίσιο της
συμμόρφωσής του με τις έννομες υποχρεώσεις του (άρθρο 6, παράγραφος 1(γ) του ΓΚΠΔ), όπως αυτές
καθορίζονται από την ΚΥΑ ΥΠΕΝ/ΓΔΕ/49043/1893/07.05.2025 (ΦΕΚ Β’ 2212/07.05.2025).
V. Αποδέκτες προσωπικών δεδομένων
Τα ανωτέρω δεδομένα συλλέγονται μέσω της εν λόγω πλατφόρμας και αποθηκεύονται στις
πληροφοριακές υποδομές της Διεύθυνσης Πληροφορικής και Ψηφιοποίησης (ΔΠΛΨ) της ΔΕΔΔΗΕ Α.Ε.
Πρόσβαση στα δεδομένα προσωπικού χαρακτήρα έχει αποκλειστικά και μόνο εξουσιοδοτημένο
προσωπικό της Εταιρείας, το οποίο δεσμεύεται με ρήτρες εμπιστευτικότητας και έχει ειδικά
δικαιώματα εξουσιοδοτημένης πρόσβασης στα δεδομένα αυτά.
Η Εταιρεία παρέχει πρόσβαση μεμονωμένα και για καθορισμένη χρονική περίοδο σε πάροχο
υπηρεσιών με την επωνυμία ΟΥΙΜΕΤΡΙΞ ΑΝΩΝΥΜΗ ΕΤΑΙΡΕΙΑ ΥΠΟΣΤΗΡΙΞΗΣ ΕΠΙΧΕΙΡΗΜΑΤΙΚΩΝ
ΑΠΟΦΑΣΕΩΝ (Wemetrix), που λειτουργεί ως «Εκτελών την Επεξεργασία», για τις ανάγκες τεχνικής
υποστήριξης του συστήματος. Στην περίπτωση αυτή, ο πάροχος υπηρεσιών ορίζει ρητά ειδικά
εξουσιοδοτημένα από αυτόν πρόσωπα τα οποία ομοίως δεσμεύονται με ρήτρες εμπιστευτικότητας
και έχουν ειδικά δικαιώματα εξουσιοδοτημένης πρόσβασης στα δεδομένα αυτά.
Η Εταιρεία δεν επιτρέπει σε τρίτους να αποκτήσουν πρόσβαση στα δεδομένα προσωπικού χαρακτήρα,
παρά μόνο εφόσον αυτό είναι απολύτως αναγκαίο για τη θεμελίωση, άσκηση ή υποστήριξη νομικών
αξιώσεών της ή για την εκπλήρωση των υποχρεώσεών της που απορρέουν από την υφιστάμενη
νομοθεσία.
Ο ΔΕΔΔΔΗΕ δεν διαβιβάζει δεδομένα προσωπικού χαρακτήρα των χρηστών σε τρίτη χώρα ή διεθνή
οργανισμό.
VI. Εγγυήσεις ασφάλειας των δεδομένων
Ο ΔΕΔΔΗΕ και ο Εκτελών την επεξεργασία λαμβάνουν τα κατάλληλα τεχνικά και οργανωτικά μέτρα για
να εξασφαλίσουν την προστασία των προσωπικών δεδομένων σύμφωνα με τις αντίστοιχες
κανονιστικές απαιτήσεις και τις σύγχρονες καλές πρακτικές του, για την πρόληψη παραβίασης λόγω
κακόβουλων ενεργειών ή ακούσιας απώλειας, μη εξουσιοδοτημένης πρόσβασης ή χρήσης αυτών.
VII. Χρόνος διατήρησης προσωπικών δεδομένων
Το χρονικό διάστημα διατήρησης των ως άνω προσωπικών δεδομένων διαμορφώνεται με βάση τις
αρχές που διέπουν την επεξεργασία δεδομένων προσωπικού χαρακτήρα και τα προβλεπόμενα ιδίως
στο άρθρο 5 του ΓΚΠΔ, κατά συνεκτίμηση και των αρμοδιοτήτων και του ρόλου της ΔΕΔΔΗΕ Α.Ε., με
βάση το κείμενο νομικό πλαίσιο.
Τα ανωτέρω δεδομένα θα τηρούνται από τον ΔΕΔΔΗΕ για το χρονικό διάστημα που απαιτείται για την
εκπλήρωση του σκοπού για τον οποίο συνελέγησαν και διαγράφονται μετά το πέρας αυτής, σύμφωνα
με τους γενικούς όρους χρήσης της πλατφόρμας, την Πολιτική Προστασίας Δεδομένων Προσωπικού
Χαρακτήρα του ΔΕΔΔΗΕ και την εφαρμοστέα νομοθεσία. Τα δεδομένα αυτά είναι κάθε φορά συναφή,
πρόσφορα και όχι περισσότερα από όσα απαιτούνται εν όψει των ανωτέρω σκοπών, είναι δε ακριβή
και, εφόσον χρειάζεται, υποβάλλονται σε ενημέρωση.
VIII. Τα δικαιώματά σας ως υποκειμένου των δεδομένων
Η Εταιρεία έχει την υποχρέωση βάσει του Γενικού Κανονισμού για την Προστασία Προσωπικών
Δεδομένων (ΓΚΠΔ) να διασφαλίζει την ικανοποίηση των δικαιωμάτων των υποκειμένων των
δεδομένων, διευκολύνοντας την άσκησή τους με συνοπτική, διαφανή, κατανοητή και εύκολα
προσβάσιμη μορφή.
Αναφορικά με τα προσωπικά δεδομένα σας που υποβάλλονται στην ανωτέρω περιγραφόμενη
επεξεργασία, έχετε τα ακόλουθα δικαιώματα:
Δικαίωμα ενημέρωσης & πρόσβασης (άρθρα 12 – 15 του ΓΚΠΔ): Έχετε δικαίωμα να ενημερωθείτε και
να έχετε πρόσβαση στα δεδομένα που σας αφορούν, καθώς και να λαμβάνετε συμπληρωματικές
πληροφορίες σχετικά με την επεξεργασία τους.
Δικαίωμα διόρθωσης (άρθρο 16 του ΓΚΠΔ): Έχετε δικαίωμα να ζητήσετε τη διόρθωση, τροποποίηση,
συμπλήρωση και επικαιροποίηση των δεδομένων σας, εφόσον αυτά είναι ανακριβή, ανεπίκαιρα ή
ελλιπή.
Δικαίωμα διαγραφής (άρθρο 17 του ΓΚΠΔ): Έχετε δικαίωμα ναζητήσετε τη διαγραφήτων τηρούμενων
προσωπικών σας δεδομένων όταν το εν λόγω δικαίωμα δεν υπόκειται σε περιορισμούς σύμφωνα με
την ισχύουσα νομοθεσία ή τυχόν άλλους περιορισμούς.
Δικαίωμα περιορισμού της επεξεργασίας (άρθρο 18 του ΓΚΠΔ): Έχετε δικαίωμα να ζητήσετε τον
περιορισμό της επεξεργασίας των προσωπικών σας δεδομένων όταν: (α) αμφισβητείται η ακρίβεια
των προσωπικών δεδομένων και μέχρι να γίνει επαλήθευση, (β) η επεξεργασία είναι παράνομη και
ζητάτε αντί διαγραφής τον περιορισμό χρήσης των προσωπικών σας δεδομένων, (γ) τα προσωπικά
δεδομένα δεν χρειάζονται για τους σκοπούς επεξεργασίας, σας είναι ωστόσο απαραίτητα για τη
θεμελίωση, άσκηση, υποστήριξη νομικών αξιώσεων, και (δ) εναντιώνεστε στην επεξεργασία και μέχρι
να γίνει επαλήθευση ότι υπάρχουν νόμιμοι λόγοι που αφορούν την εκάστοτε Εταιρεία και
υπερισχύουν των λόγων για τους οποίους εναντιώνεστε στην επεξεργασία.
Δικαίωμα στη φορητότητα (άρθρο 20 του ΓΚΠΔ): Έχετε δικαίωμα να λάβετε χωρίς χρέωση τα
προσωπικά σας δεδομένα σε μορφή που θα σας επιτρέπει να έχετε πρόσβαση σε αυτά, να τα
χρησιμοποιήσετε και να τα επεξεργαστείτε, καθώς και να μας ζητήσετε, εφόσον είναι τεχνικά εφικτό,
να διαβιβάσουμε τα δεδομένα σας απευθείας σε άλλον Υπεύθυνο Επεξεργασίας. Το εν λόγω δικαίωμα
ισχύει για τα δεδομένα που μας έχετε παράσχει εσείς και η επεξεργασία τους διενεργείται με
αυτοματοποιημένα μέσα με βάση της συγκατάθεσή σας ή σε εκτέλεση σχετικής σύμβασης.
Δικαίωμα εναντίωσης στην επεξεργασία (άρθρο 21 του ΓΚΠΔ): Έχετε δικαίωμα να εναντιωθείτε ανά
πάσα στιγμή στην επεξεργασία των προσωπικών σας δεδομένων υπό συγκεκριμένες μόνο
προϋποθέσεις που προβλέπονται στη νομοθεσία.
Δικαίωμα εναντίωσης στην αυτοματοποιημένη ατομική λήψη αποφάσεων, περιλαμβανομένης της
κατάρτισης προφίλ (άρθρο 22 του ΓΚΠΔ): Έχετε το δικαίωμα να εναντιωθείτε σε απόφαση που
λαμβάνεται αποκλειστικά βάσει αυτοματοποιημένης επεξεργασίας, συμπεριλαμβανομένης της
κατάρτισης προφίλ, όταν η απόφαση αυτή παράγει έννομα αποτελέσματα που σας αφορούν ή σας
επηρεάζουν σημαντικά με παρόμοιο τρόπο. Η Εταιρεία δεν προβαίνει στην παρούσα χρονική περίοδο
σε αυτοματοποιημένη ατομική λήψη αποφάσεων. Σε κάθε, όμως, περίπτωση και εφόσον στο μέλλον
αποφασίσει να προχωρήσει σε αυτοματοποιημένη ατομική λήψη αποφάσεων, θα ενημερώσει σχετικά
τα Υποκείμενα των Δεδομένων και θα τηρήσει όλες τις νόμιμες προϋποθέσεις.
Δικαίωμα στην ανάκληση της συγκατάθεσης (άρθρο 7 του ΓΚΠΔ): Έχετε δικαίωμα να ανακαλέσετε τη
συγκατάθεσή σας, στο μέτρο που ελήφθη για τις σκοπούμενες επεξεργασίες, ανά πάσα στιγμή.
Δικαίωμα καταγγελίας στην αρμόδια αρχή: Έχετε το δικαίωμα να προβείτε σε καταγγελία ενώπιον
της αρμόδιας εποπτικής αρχής, η οποία για την Ελλάδα είναι η Αρχή Προστασίας Δεδομένων
Προσωπικού Χαρακτήρα
<span>(για περισσότερες πληροφορίες: <a href="https://www.dpa.gr/el/polites/katagelia_stin_arxi" target="_blank">https://www.dpa.gr/el/polites/katagelia_stin_arxi</a>)</span> .
ΙΧ. Επικοινωνία
Για την άσκηση των ως άνω δικαιωμάτων σας ως υποκειμένου των δεδομένων, μπορείτε να
απευθύνεστε στον Υπεύθυνο Προστασίας Δεδομένων (Data Protection Officer) του ΔΕΔΔΗΕ, στην
ηλεκτρονική διεύθυνση dpo@deddie.gr ή στην ταχυδρομική διεύθυνση: Λ. Συγγρού 98-100, 11741,
Αθήνα, υπόψιν ΔΕΔΔΗΕ Υπευθύνου Προστασίας Δεδομένων (DPO).
`;
    // Initialize personal info form
    this.initPersonalInfoForm();
  }
  
  // Initialize personal info form with validators
  initPersonalInfoForm(): void {
    this.personalInfoForm = this.fb.group({
      name: ['', [
        Validators.required, 
        Validators.pattern(/^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]+$/)
      ]],
      surname: ['', [
        Validators.required, 
        Validators.pattern(/^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]+$/)
      ]],
     email: ['', [
  Validators.required, 
  Validators.email,
  Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
]],
mobileNumber: ['', [
    Validators.required,
    Validators.pattern(/^69[0-9]{8}$/),  // Requires starting with 69 and 8 more digits (total 10)
    Validators.minLength(10),
    Validators.maxLength(10)
]]
    });
    
    // Set up value change listeners to convert text to uppercase
    this.personalInfoForm.get('name')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        this.personalInfoForm.get('name')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.personalInfoForm.get('surname')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        this.personalInfoForm.get('surname')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });
  }


 //  'https://test.gsis.gr/oauth2server/logout/T34U0U33935?redirect_uri=' 
        // redirectUri:   'https://test.gsis.gr/oauth2server/logout/T34U0U33935?redirect_uri=http://localhost:4200'
    // Simple direct method to call logout on keycloak instance with POST method
  logoutKeycloak() {

    
    if (keycloakInstance) {
      keycloakInstance.logout({
        redirectUri: window.location.origin,
        logoutMethod: 'POST'
      });
    }
  }

  // Terms popup scrolling handling
onTermsScroll(event: Event, step: number): void {
  const element = event.target as HTMLElement;
  const scrollPosition = element.scrollTop;
  const scrollHeight = element.scrollHeight;
  const clientHeight = element.clientHeight;
  
  // Check if scrolled to bottom (with a small tolerance)
  const isAtBottom = scrollHeight - scrollPosition - clientHeight < 10;
 
  if (step === 1 && isAtBottom) {
    this.firstTermsScrolled = true;
    this.cdr.detectChanges();
  } else if (step === 2 && isAtBottom) {
    this.secondTermsScrolled = true;
    this.cdr.detectChanges();
  }
}

  // Terms progression methods
  nextStep(): void {
    if (this.currentStep === 1 && this.firstTermsAccepted) {
      this.currentStep = 2;
      
      // Reset scroll position for second terms
      setTimeout(() => {
        if (this.secondTermsScrollElement) {
          this.secondTermsScrollElement.nativeElement.scrollTop = 0;
        }
      }, 0);
      
      this.cdr.detectChanges();
    }
  }

  completeTerms(): void {
    if (this.secondTermsAccepted) {
      // Hide terms and move to declaration step
      this.showTerms = false;
      this.currentStep = 3;
      this.cdr.detectChanges();
    }
  }
  
  // Declaration methods
  allConfirmationsChecked(): boolean {
    const requiredItems = this.confirmationItems.filter((_, index) => index !== 3);
    const unifiedCompanyChecked = this.confirmationItems[3].checked;
    
    if (unifiedCompanyChecked && this.relatedCompanies.length === 0) {
      this.addRelatedCompany();
    }
    
    return requiredItems.every(item => item.checked);
  }
  
/**
 * Adds a new related company and unchecks final confirmation
 */
addRelatedCompany() {
  this.relatedCompanies.push({ vat: '', name: '' });
  // Uncheck final confirmation when adding a new company
  this.finalConfirmChecked = false;
}

/**
 * Removes a related company and unchecks final confirmation
 * @param index Index of the company to remove
 */
removeRelatedCompany(index: number) {
  this.relatedCompanies.splice(index, 1);
  // Uncheck final confirmation when removing a company
  this.finalConfirmChecked = false;
}
  

  
  onRelatedCompanyToggle() {
    if (this.confirmationItems[3].checked && this.relatedCompanies.length === 0) {
      this.addRelatedCompany();
    } else if (!this.confirmationItems[3].checked) {
      this.relatedCompanies = [];
    }
  }
  

  onKeyDownLettersOnly(event: KeyboardEvent): boolean {
    // Allow navigation, deletion, etc.
    if (this.isControlKey(event)) {
      return true;
    }
    const regex = /^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]$/;
    
    // Test if the pressed key matches the pattern
    if (!regex.test(event.key)) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Key handling for mobile number (digits only)
  onKeyDownDigitsOnly(event: KeyboardEvent): boolean {
    // Allow navigation, deletion, etc.
    if (this.isControlKey(event)) {
      return true;
    }

    // Check if the key is a digit
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Helper method to check if a key is a control key
  private isControlKey(event: KeyboardEvent): boolean {
    // Control keys: navigation, deletion, etc.
    return event.key === 'Backspace' || 
           event.key === 'Delete' || 
           event.key === 'Tab' || 
           event.key === 'Escape' || 
           event.key === 'Enter' || 
           event.key === 'Home' || 
           event.key === 'End' || 
           event.key === 'ArrowLeft' || 
           event.key === 'ArrowRight' || 
           event.key === 'ArrowUp' || 
           event.key === 'ArrowDown' || 
           (event.ctrlKey === true) || // Allow Ctrl+C, Ctrl+V, etc.
           (event.metaKey === true);   // Allow Command key on Mac
  }
  
  // Convert input to uppercase immediately on input
  convertToUppercase(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    this.personalInfoForm.get(controlName)?.setValue(input.value);
  }
submitPersonalInfo(): void {
  if (this.personalInfoForm.invalid) {
    // Mark all fields as touched to trigger validation messages
    Object.keys(this.personalInfoForm.controls).forEach(key => {
      const control = this.personalInfoForm.get(key);
      control?.markAsTouched();
    });
    return;
  }

  this.isFormSubmitting = true; // Show form submitting spinner
  this.loadingProgress = false; // Make sure the global overlay is not showing yet
  this.formErrorMessage = '';

  // Prepare the data for submission
  const personalInfo: PersonalInfoRequest = {
    first_name: this.personalInfoForm.get('name')?.value,
    last_name: this.personalInfoForm.get('surname')?.value,
    mobile_number: this.personalInfoForm.get('mobileNumber')?.value,
    email: this.personalInfoForm.get('email')?.value
  };

  // Call the service to submit the data
  this.validationService.submitPersonalInfo(personalInfo).subscribe({
    next: (response) => {
      
        console.log('Personal info submitted successfully:', response);
      
        // Stop showing form spinner, switch to global progress
        this.isFormSubmitting = false;
        this.loadingProgress = true;
        
      // Store any error message for display on the meters page if needed
      if (response && response.error) {
        this.formErrorMessage = response.error.message || 'Error with your information. Please check and try again.';
      }
      
      // Check for previous applications and proceed to meters page
      this.checkPreviousApplicationsImmediate();
    },
    error: (error) => {
      // For network/unhandled errors, still proceed to meters page
      this.isFormSubmitting = false;
      this.loadingProgress = true;
      
      // Store error message for display on the meters page
      this.formErrorMessage = 'An unexpected error occurred. Please try again later.';
      console.error('Unhandled error in personal info submission:', error);
      
      // Continue to meters page anyway
      this.checkPreviousApplicationsImmediate();
    }
  });
}


  continueAfterFormSuccess(): void {
    this.isFormSuccess = false;
    this.formErrorMessage = '';
    
    // Proceed to the next step - get meters
    this.getAllUserMeters();
}

getAllUserMeters() {
  // loadingProgress should be set by the calling method
  
  // Check for previous submissions - this might be redundant now but keeping for safety
  this.validationService.checkPreviousInput().subscribe({
    next: (res) => {
      console.log('Previous applications check in getAllUserMeters:', res);
      // If we get any array with items, user has previous applications
      this.hasPreviousApplications = res && Array.isArray(res) && res.length > 0;
      this.previousApplications = this.hasPreviousApplications ? res : [];
    },
    error: (error) => {
      console.error('Error checking previous applications in getAllUserMeters:', error);
      this.hasPreviousApplications = false;
    }
  });
  
  // Now fetch the meters - loadingProgress already true
  this.validationService.getAllUserMeters().subscribe({
    next: (res) => {
      // Always turn off loading when we get a response
      this.loadingProgress = false;
      
      // Handle the case where we get an error message in the response
      if (res && res.error && (res.is_eligible === false || res.supply_list === "empty")) {
        console.log('Error in meter eligibility:', res.error);
        this.formErrorMessage = res.error || 'Δεν υπάρχουν επιλέξιμες παροχές.';
        this.title = 'Η ΑΙΤΗΣΗ ΣΑΣ';
        this.inputs = []; // Clear any existing inputs
        
        // Move to meters step to display the error
        this.currentStep = 5;
        this.cdr.detectChanges();
        return;
      }
      
      // Normal successful response processing
      if (res && res.supply_list && Array.isArray(res.supply_list) && res.supply_list.length > 0) {
        // Clear any previous error messages
        this.formErrorMessage = '';
        
        // Map the response to our input model, including the usage type (xrhsh)
        this.inputs = res.supply_list.map((supply: any, index: number) => ({
          id: index + 1,
          value: supply.supplyNumber,
          selected: false,
          xrhsh: supply.xrhsh,  // Include usage type
          type: supply.type     // Include meter type
        }));
        
        console.log(`Retrieved ${this.inputs.length} meters`);
      } else {
        // No meters available or empty response
        console.log('No eligible meters found');
        this.inputs = []; // Ensure the inputs array is empty
        this.formErrorMessage = ''; // No error message for legitimately empty results
        this.title = 'Η ΑΙΤΗΣΗ ΣΑΣ';
      }
      
      // Move to the meters selection step
      this.currentStep = 5;
      this.checkSelectionStatus();
    },
    error: (error) => {
      console.error('Error fetching meter data:', error);
      // Always turn off loading on error
      this.loadingProgress = false;
      this.title = 'Η ΑΙΤΗΣΗ ΣΑΣ';
      
      // Display the error message to the user
      this.formErrorMessage = error.error?.error || error.message || 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των παροχών.';
      this.inputs = []; // Clear any existing inputs
      
      // Move to the meters selection step to show the error
      this.currentStep = 5;
      this.cdr.detectChanges();
    }
  });
}


closeRecallDialog(): void {
  this.showRecallDialog = false;
}

closeSuccessDialog(): void {
  this.showSuccessDialog = false;
  
  // After user acknowledges the recall success/failure, proceed with fetching data
  this.loadingProgress = true;
  this.getAllUserMeters();
}


get isAtLeastOneSelected(): boolean {
  // Return false if there are no meters at all
  if (this.inputs.length === 0) {
    return false;
  }
  
  // Otherwise, check if at least one meter is selected
  return this.inputs.some(input => input.selected);
}
  
/**
 * Updates all selectable meters when the "Select All" checkbox is clicked
 * Residential meters (Οικιακή) remain unselected
 */
onSelectAllChange() {
  // Apply selection only to non-residential meters
  this.inputs.forEach(input => {
    // Only update selectable meters (non-residential)
    if (input.xrhsh !== 'Οικιακή') {
      input.selected = this.selectAll;
    }
  });
  this.checkSelectionStatus();
}

/**
 * Updates the "Select All" checkbox state based on current selections
 * Only considers selectable meters (non-residential)
 */
onCheckboxChange() {
  // Count only non-residential meters for the "Select All" state
  const selectableMeters = this.inputs.filter(input => input.xrhsh !== 'Οικιακή');
  
  // If all selectable meters are checked, then "Select All" should be checked
  this.selectAll = selectableMeters.length > 0 && 
                   selectableMeters.every(input => input.selected);
  
  this.checkSelectionStatus();
}
  
  checkSelectionStatus() {
    // Kept for compatibility
  }
  

getSelectedMeterIds(): string[] {
  return this.inputs
    .filter(input => input.selected)
    .map(input => input.value);
}

// Final submission - called when the user submits the meters selection
private selectedMeterParams: HttpParams | null = null;

// Modify the onSubmit method to store the params
onSubmit() {
  if (!this.isAtLeastOneSelected) {
    return;
  }
  
  this.loadingProgress = true;
  
  // Get selected meter values (the actual meter numbers)
  const selectedMeterValues = this.getSelectedMeterIds();
  
  // Build params for the request
  let params = new HttpParams();
  selectedMeterValues.forEach(value => {
    params = params.append('meter_id', value);
  });
  
  // Process selected meters for navigation
  const selectedMeters = this.inputs
    .filter(input => input.selected)
    .reduce((acc, input) => {
      acc[input.value] = input.value;
      return acc;
    }, {} as Record<string, string>);
  
  // Store selected meters in the service
  this.meterService.updateMeters(selectedMeters);
  
  // Store the parameters in the meter service for future use
  this.meterService.storeSelectedParams(params);
  
  console.log('Selected meters prepared for eligibility check:', selectedMeters);
  console.log('Parameters stored for future recall if needed:', params.toString());
  
  // Navigate to check-eligibility without making API call
  this.loadingProgress = false;
  this.router.navigate(['/check-eligibility']);
}


/**
 * Validates the input for AFM field and shows appropriate errors
 * Also unchecks the final confirmation checkbox when editing
 * @param event Input event
 * @param company The company object being updated
 */
validateAfmInput(event:any, company:any) {
    // Your existing code here
    
    // Add AFM validation
    if (company.vat && company.vat.length === 9) {
      this.isValidAFM = this.validateAFM(company.vat);
    } else {
      this.isValidAFM = true; // Don't show the validation error until we have 9 digits
    }
  }

  // Add the validateAFM method to your component
  validateAFM(afm:any) {
    if (!afm) return false;
    if (!afm.match(/^\d{9}$/) || afm == '000000000')
      return false;
    
    var m = 1, sum = 0;
    for (var i = 7; i >= 0; i--) {
      m *= 2;
      sum += afm.charAt(i) * m;
    }
    
    return sum % 11 % 10 == afm.charAt(8);
  }



/**
 * Validates and sanitizes text input for company name field
 * Also unchecks the final confirmation checkbox when editing
 * @param event Input event
 * @param field Field name (name)
 * @param company Company object to update
 */
validateTextInput(event: Event, field: string, company: any): void {
  const inputElement = event.target as HTMLInputElement;
  const previousValue = company[field];
  
  // Keep only letters, spaces, and punctuation commonly used in company names
  const sanitizedValue = inputElement.value.replace(/[^a-zA-ZΑ-Ωα-ωίϊΐόάέύϋΰήώ\s\.\,\-\&\']/g, '');
  
  // Update the input value if it was changed
  if (inputElement.value !== sanitizedValue) {
    inputElement.value = sanitizedValue;
  }
  
  // Update the model
  company[field] = sanitizedValue;
  
  // If the value has changed, uncheck the final confirmation checkbox
  if (previousValue !== company[field]) {
    this.finalConfirmChecked = false;
    this.cdr.detectChanges(); // Force UI update
  }
}

validateNumericInput(event: Event, field: string, company: any): void {
  const inputElement = event.target as HTMLInputElement;
  // Remove any non-numeric characters
  const sanitizedValue = inputElement.value.replace(/[^0-9]/g, '');
  
  // Limit to 9 digits
  const truncatedValue = sanitizedValue.substring(0, 9);
  
  // Update the input value if it was changed
  if (inputElement.value !== truncatedValue) {
    inputElement.value = truncatedValue;
  }
  
  // Update the model
  company[field] = truncatedValue;
}

/**
 * Validates and sanitizes text input for company name field
 * @param event Input event
 * @param field Field name (name)
 * @param company Company object to update
 */



companyLabel       = 'Συνιστά ενιαία επιχείρηση με άλλη επιχείρηση (ορισμός στην ΚΥΑ)';
finalConfirmLabel  = 'Τα στοιχεία που δήλωσα είναι ακριβή και αληθή';

unifiedCompanySelection: 'yes' | 'no' | null = null;
finalConfirmChecked = false;





get areRelatedCompaniesValid(): boolean {
  if (this.unifiedCompanySelection !== 'yes') { 
    return true; 
  }
  
  if (this.relatedCompanies.length === 0) {
    return false;
  }
  
  return this.relatedCompanies.every(c => 
    c.vat && c.vat.trim().length === 9 && 
    c.name && c.name.trim().length > 0
  );
}
canEnableFinalConfirm(): boolean {
  const basicChecksValid = this.confirmationItems.slice(0, 3).every(c => c.checked);
  const companySelectionValid = this.unifiedCompanySelection !== null;
  const relatedCompaniesValid = this.areRelatedCompaniesValid;
  
  return basicChecksValid && companySelectionValid && relatedCompaniesValid;
}

canSubmit(): boolean {
  return this.canEnableFinalConfirm() && this.finalConfirmChecked;
}

/**
 * Handles changes to unified company selection (Yes/No radio buttons)
 */
onUnifiedCompanyChange(): void {
  // Update the checkbox based on radio selection
  this.confirmationItems[3].checked = (this.unifiedCompanySelection === 'yes');
  
  // Reset related companies based on selection
  if (this.unifiedCompanySelection === 'yes' && this.relatedCompanies.length === 0) {
    this.addRelatedCompany();
  } else if (this.unifiedCompanySelection === 'no') {
    this.relatedCompanies = [];
  }

  // Always uncheck the final confirmation when changing this selection
  this.finalConfirmChecked = false;
  
  // Force UI update
  this.cdr.detectChanges();
}

submitConfirmations(): void {
  if (!this.canSubmit()) {
    return;
  }

  this.loadingProgress = true;

  const declarationRequest: DeclarationRequest = {
    subsidy : this.confirmationItems[0].checked,
    sector  : this.confirmationItems[1].checked,
    energy  : this.confirmationItems[2].checked,
    company : this.unifiedCompanySelection === 'yes',
    confirm : this.finalConfirmChecked
  };

  if (this.unifiedCompanySelection === 'yes') {
    declarationRequest.pair = this.relatedCompanies.map(rc => ({
      vat_number: rc.vat,
      company   : rc.name
    }));
  }

  this.validationService.submitDeclaration(declarationRequest).subscribe(
    (response) => {
      console.log('Declaration response:', response);
      const isSuccess = response && response.success !== false;

      if (isSuccess) {
        console.log('Declaration submission successful');
        this.loadingProgress = false;
        this.currentStep = 4;
        this.cdr.detectChanges();
      } else {
        console.error('Declaration submission failed:', response);
        this.loadingProgress = false;
        this.cdr.detectChanges();
      }
    },
    (error) => {
      console.error('Unhandled error in submitDeclaration:', error);
      this.loadingProgress = false;
      this.cdr.detectChanges();
    }
  );
}

/**
 * Navigate back to the declarations/confirmations form
 */
goBackToDeclarations(): void {
  // First scroll to top of page with smooth animation
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  
  // Set a small timeout to ensure scroll completes before changing view
  setTimeout(() => {
    // Set the current step to 3 (declarations/confirmations)
    this.currentStep = 3;
    
    // Force UI update
    this.cdr.detectChanges();
  }, 300); // Small delay to allow scroll animation to start
}

 proceedToNext(): void {
    this.showConfirmationPopup();
  }

   // Method to show the popup
  showConfirmationPopup(): void {
    this.logoutcomplete();
  }

  logoutcomplete(){
    this.validationService.completeResults().subscribe(res=>{
     this.requestID=res.aa_number
     this.showPopup = true;
    })
  }

  // Method to check for previous applications immediately
checkPreviousApplicationsImmediate(): void {
  // Loading indicator should already be on from the calling method
  
  this.validationService.checkPreviousInput().subscribe({
    next: (res) => {
      console.log('Previous applications:', res);
      
      // Check if we have valid application data
      if (res && Array.isArray(res) && res.length > 0) {
        // Get the LAST element of the array (most recent application)
        const latestApplication = res[res.length - 1];
        
        // Make sure the latest application has valid subsidy data
        if (latestApplication && latestApplication.subsidy && 
            Array.isArray(latestApplication.subsidy) && 
            latestApplication.subsidy.length > 0) {
          
          // Store the previous application data
          this.previousApplication = latestApplication as PreviousApplication;
          
          // Set requestID if it's not already set
          if (!this.requestID) {
            this.requestID = this.previousApplication.aa_number || 
                             this.previousApplication.submit_number || 
                             'N/A';
            console.log('Request ID from previous application:', this.requestID);
          }
          
          // Set hasPreviousApplications to true
          this.hasPreviousApplications = true;
          
          // Always turn off loading before showing UI elements
          this.loadingProgress = false;
          
          // Show the immediate recall prompt
          this.showImmediateRecallPrompt = true;
          console.log('Showing immediate recall prompt with data:', this.previousApplication);
          this.cdr.detectChanges();
          return;
        } else {
          // Latest application has no valid subsidy data
          this.hasPreviousApplications = res.some(app => 
            app.subsidy && Array.isArray(app.subsidy) && app.subsidy.length > 0);
          console.log('Latest application has no valid subsidy data, hasPreviousApplications:', this.hasPreviousApplications);
        }
      } else {
        // No previous applications
        console.log('No previous applications found');
        this.hasPreviousApplications = false;
      }
      
      // IMPORTANT: Continue with normal flow - get all meters
      // Turn off loading only when fetchValidationResults is called within getAllUserMeters
      this.getAllUserMeters();
    },
    error: (error) => {
      console.error('Error checking previous applications:', error);
      this.hasPreviousApplications = false;
      
      // IMPORTANT: Always turn off loading on error
      this.loadingProgress = false;
      
      // Proceed with normal flow on error
      this.getAllUserMeters();
    }
  });
}

  // Close immediate recall prompt and proceed with normal flow
  // closeImmediateRecallPrompt(recallConfirmed: boolean): void {
  //   if(recallConfirmed===false){
  //     this.logoutKeycloak();
  //   }
  //   this.showImmediateRecallPrompt = false;
    
  //   if (recallConfirmed) {
  //     // If user confirmed recall, send recall request with parameters
  //     this.sendRecallRequest();
  //   } else {
  //     // Otherwise, proceed with normal flow
  //     this.fetchValidationResults();
  //   }
  // }

  // // Send recall request with stored parameters
  // sendRecallRequest(): void {
  //   this.loadingProgress = true;
  //   this.showRecallDialog = false;
  //   this.showImmediateRecallPrompt = false;
    
  //   // Get the stored parameters from the meter service
  //   const params = this.meterService.getStoredParams();
    
  //   this.validationService.sendResetMessage().subscribe(
  //     (response) => {
  //       this.loadingProgress = false;
  //       console.log('Recall request successful:', response);
        
  //       // Show success message
  //       this.successMessage = 'Η ανάκληση της προηγούμενης αίτησης ολοκληρώθηκε με επιτυχία.';
  //       this.showSuccessDialog = true;
        
  //       // Update the previous applications status
  //       this.hasPreviousApplications = false;
        
  //       // Fetch validation results after successful recall
  //       this.fetchValidationResults();
  //     },
  //     (error) => {
  //       this.loadingProgress = false;
  //       console.error('Recall request failed:', error);
        
  //       // Show error message
  //       this.successMessage = 'Η ανάκληση της προηγούμενης αίτησης απέτυχε. Παρακαλώ δοκιμάστε ξανά.';
  //       this.showSuccessDialog = true;
        
  //       // Fetch validation results anyway
  //       this.fetchValidationResults();
  //     }
  //   );
  // }

  // Open recall dialog with parameter support
  openRecallDialog(): void {
    // Show loading while checking
    this.loadingProgress = true;
    
    // Store the current requestID value before potentially changing it
    const currentRequestID = this.previousRequestID;
    
    // Make an explicit API call to check for previous applications
    this.validationService.checkPreviousInput().subscribe(
      (res) => {
        this.loadingProgress = false; // Hide loading after check completes
        console.log('Explicit check for previous applications before opening dialog:', res);
        
        // Check if we have any valid applications with subsidy data
        if (res && Array.isArray(res) && res.length > 0) {
          // Check if any application has subsidy data
          this.hasPreviousApplications = res.some(app => app.subsidy && Array.isArray(app.subsidy) && app.subsidy.length > 0);
          
          // Store all applications
          this.previousApplications = res;
          
          // If we have previous applications, get the latest one for the dialog
          if (this.hasPreviousApplications) {
            // Use the LAST element as the latest application
            const latestApp = res[res.length - 1];
            
            // Update previousApplication to show the latest one in the dialog
            this.previousApplication = latestApp as PreviousApplication;
            
            // Restore the requestID to its original value if it was already set
            if (currentRequestID) {
              this.previousRequestID = currentRequestID;
              console.log('Restoring original Request ID:', this.requestID);
            } else {
              // Only set it from latest application if not already set
              this.previousRequestID = latestApp.aa_number || latestApp.submit_number || 'N/A';
              console.log('Setting Request ID from latest application:', this.requestID);
            }
          }
        } else {
          this.hasPreviousApplications = false;
          this.previousApplications = [];
        }
        
        console.log('Has previous applications based on explicit check:', this.hasPreviousApplications);
        
        // Only show the dialog if there are actual previous applications
        if (this.hasPreviousApplications) {
          this.showRecallDialog = true;
        } else {
          console.log('No previous applications found, not showing recall dialog');
        }
      },
      (error) => {
        this.loadingProgress = false; // Hide loading on error
        console.error('Error during explicit check for previous applications:', error);
        this.hasPreviousApplications = false;
        // Don't show the dialog if there was an error checking
        
        // Make sure to restore the original requestID
       
      }
    );
  }

 fetchValidationResults(): void {
    this.isLoading = true;
    this.errorMessage = '';
  this.hasError = false; // Reset error state at the beginning

    // Get the stored parameters that were prepared in InputMetersComponent
    const params = this.meterService.getStoredParams();
    
    // Check if parameters exist
    if (!params || params.keys().length === 0) {
      this.isLoading = false;
      this.errorMessage = 'Δεν βρέθηκαν παράμετροι για τις επιλεγμένες παροχές.';
    this.hasError = true; // Set error flag
    this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
      console.error('No parameters found for validation');
      return;
    }

    console.log('Making API call with stored parameters:', params.toString());

    // Make the actual API call with the stored parameters
    this.meterService.validateSelectedMeters(params).subscribe(
      (results: any) => {
        this.isLoading = false;
    
      // Check if we received an error response object
      if (results && results.status === 'error') {
        this.errorMessage = results.description || 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των αποτελεσμάτων.';
        this.hasError = true; // Set error flag
            this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
        console.error('API returned error:', results);
        return;
      }
    
        if (results && results.length > 0) {
          // Extract and store the submit_number if present
          const submitNumberEntry = results.find((item: any) => item.hasOwnProperty('submit_number'));
          if (submitNumberEntry) {
            this.requestID = submitNumberEntry.submit_number;
            console.log('Request ID from validation results:', this.requestID);
          }
          
          // Filter out the submit_number entry and keep only the meter entries
          this.validationResults = results.filter((item: any) => item.hasOwnProperty('meter_id'));
          console.log('Retrieved validation results:', this.validationResults);
          
          if (this.validationResults.length > 0) {
            this.processValidationResults();
          } else {
            this.errorMessage = 'Δεν βρέθηκαν αποτελέσματα για τις επιλεγμένες παροχές.';
          this.hasError = true; // Set error flag
              this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
            console.log('No validation results available');
          }
        } else {
          this.errorMessage = 'Δεν βρέθηκαν αποτελέσματα για τις επιλεγμένες παροχές.';
        this.hasError = true; // Set error flag
            this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
          console.log('No validation results available');
        }
      },
      (error) => {
        this.isLoading = false;
      this.hasError = true; // Set error flag
          this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
    
      // Check if the error response contains the specific error structure
      if (error && error.error) {
        // The error might be in error.error due to how HttpClient wraps errors
        if (error.error.status === 'error' && error.error.description) {
          this.errorMessage = error.error.description;
              this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
          console.error('API error with description:', error.error);
          return;
        }
      }
    
      // Default error message if we can't extract a specific one from the response
        this.errorMessage = 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των αποτελεσμάτων.';
            this.title='Η ΑΙΤΗΣΗ ΣΑΣ'
        console.error('Error fetching validation results:', error);
      }
    );
  }


  
  // Process validation results into meter details for display
  processValidationResults(): void {
    this.meterDetails = [];
    const processedMeters = new Map<string, MeterDetails>();

    // Process each response item
    for (const item of this.validationResults) {
      // Skip items that don't have meter_id (like submit_number)
      if (!item.meter_id || !item.subsidy_info) {
        continue;
      }

      const meterId = item.meter_id;
      const subsidyInfo = item.subsidy_info;
      
      // Determine if meter is eligible based on status
      const isEligible = subsidyInfo.status === 'success';
      
      // Format subsidy amount - show "-" for pending status
      let formattedSubsidy = '0';
      
      if (subsidyInfo.status === 'pending') {
        formattedSubsidy = '-';
      } else if (subsidyInfo.total_subsidy) {
        formattedSubsidy = subsidyInfo.total_subsidy.toFixed(2).replace('.', ',');
      }
      
      // Extract KAD information if available
      let kad = '-';
      let kadDescription = '-';
      if (subsidyInfo.business_activity_code && typeof subsidyInfo.business_activity_code === 'object') {
        kad = subsidyInfo.business_activity_code.kad || '-';
        kadDescription = subsidyInfo.business_activity_code.description || '-';
      }
      
      // Create meter details with new fields
      const meterDetail: MeterDetails = {
        id: meterId,
        value: meterId,
        eligible: isEligible,
        explanation: subsidyInfo.description || 'Δεν υπάρχει διαθέσιμη επεξήγηση.',
        subsidyAmount: formattedSubsidy,
        status: subsidyInfo.status || 'unknown',
        // Add new fields
        kad: kad,
        kadDescription: kadDescription,
        consumption: subsidyInfo.consumption_kwh || 0,
        subsidyPerKwh: subsidyInfo.subsidy_per_kwh || 0,
        applicable_ratio: subsidyInfo.applicable_ratio // Extract applicable_ratio from subsidyInfo
      };

      // Add to processed map
      processedMeters.set(meterId, meterDetail);
    }

    // Convert map to array for template use
    this.meterDetails = Array.from(processedMeters.values());
    
    // If no results were processed, set error message
    if (this.meterDetails.length === 0) {
      this.errorMessage = 'Δεν βρέθηκαν πληροφορίες επιδότησης για τις επιλεγμένες παροχές.';
    }
  }

    formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('el-GR');
    } catch (e) {
      return dateString;
    }
  }







// New method that performs previous applications check first
// and only proceeds to meter fetching if appropriate
checkPreviousApplicationsBeforeProceeding(): void {
  // Keep loading indicator on during this check
  this.isFormSubmitting = true;
  
  this.validationService.checkPreviousInput().subscribe(
    (res) => {
      console.log('Previous applications:', res);
      
      // Check if we have valid application data
      if (res && Array.isArray(res) && res.length > 0) {
        // Get the LAST element of the array (most recent application)
        const latestApplication = res[res.length - 1];
        
        // Make sure the latest application has valid subsidy data
        if (latestApplication && latestApplication.subsidy && 
            Array.isArray(latestApplication.subsidy) && 
            latestApplication.subsidy.length > 0) {
          
          // Store the previous application data
          this.previousApplication = latestApplication as PreviousApplication;
          
          // Set requestID if it's not already set
          if (!this.requestID) {
            this.requestID = this.previousApplication.aa_number || 
                            this.previousApplication.submit_number || 
                            'N/A';
            console.log('Request ID from previous application:', this.requestID);
          }
          
          // Set hasPreviousApplications to true
          this.hasPreviousApplications = true;
          
          // Stop the loading indicator before showing the prompt
          this.isFormSubmitting = false;
          
          // Show the immediate recall prompt
          this.showImmediateRecallPrompt = true;
          console.log('Showing immediate recall prompt with data:', this.previousApplication);
          
          // Important: We don't proceed to getAllUserMeters here
          // Let the user decide via the prompt
          this.cdr.detectChanges();
          return;
        } else {
          // Latest application has no valid subsidy data, check if any application has valid subsidy
          this.hasPreviousApplications = res.some(app => 
            app.subsidy && Array.isArray(app.subsidy) && app.subsidy.length > 0);
          console.log('Latest application has no valid subsidy data, hasPreviousApplications:', this.hasPreviousApplications);
        }
      } else {
        // No previous applications
        console.log('No previous applications found');
        this.hasPreviousApplications = false;
      }
      
      // If we reach here, no immediate recall is needed
      // Continue with normal flow - get all meters
      this.isFormSubmitting = false;
      this.getAllUserMeters();
    },
    (error) => {
      console.error('Error checking previous applications:', error);
      this.hasPreviousApplications = false;
      this.isFormSubmitting = false;
      
      // Proceed with normal flow on error - get all meters
      this.getAllUserMeters();
    }
  );
}

// Update closeImmediateRecallPrompt to ensure it handles form state properly
closeImmediateRecallPrompt(recallConfirmed: boolean): void {
  this.showImmediateRecallPrompt = false;
  
  if (recallConfirmed === false) {
    this.logoutKeycloak();
    return; // Exit early to prevent further processing
  }
  
  if (recallConfirmed) {
    // If user confirmed recall, send recall request with parameters
    // Show loading indicator during recall process
    this.loadingProgress = true;
    this.sendRecallRequest();
  } else {
    // Otherwise, proceed with normal flow - but we shouldn't reach here
    // because of the early return above
    this.loadingProgress = true; // Turn on loading indicator before fetching
    this.getAllUserMeters();
  }
}

sendRecallRequest(): void {
  // loadingProgress should already be true from the calling method
  
  this.validationService.sendResetMessage().subscribe({
    next: (response) => {
      console.log('Recall request successful:', response);
      
      // Turn off loading before showing dialog
      this.loadingProgress = false;
      
      // Show success message
      this.successMessage = 'Η ανάκληση της προηγούμενης αίτησης ολοκληρώθηκε με επιτυχία.';
      this.showSuccessDialog = true;
      
      // Update the previous applications status
      this.hasPreviousApplications = false;
      
      // Don't fetch validation results until user closes dialog
      // Add this handling in closeSuccessDialog method
    },
    error: (error) => {
      // Turn off loading before showing dialog
      this.loadingProgress = false;
      
      console.error('Recall request failed:', error);
      
      // Show error message
      this.successMessage = 'Η ανάκληση της προηγούμενης αίτησης απέτυχε. Παρακαλώ δοκιμάστε ξανά.';
      this.showSuccessDialog = true;
    }
  });
}

}